import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import type { Campaign as SegmentCampaign } from '@segment/analytics-core'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { useLocation } from 'react-router'

import { isAdminRoutePathname } from '@bettermode/domain/routes/web/admin'
import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'

import { RuntimeConfigs } from '../../../configs/runtime.js'
import { defaultLogger as logger } from '../../../logger/client/index.js'
import { extractUtmDataFromCookies } from '../utils/utm-cookies.utils.js'

const analytics = new AnalyticsBrowser()

const enabled = !!RuntimeConfigs.SEGMENT_WRITE_KEY
const ENVIRONMENT = 'Workspace'

/**
 * Add UTM tracker middleware
 *
 * analytics is a global object. Similarly, middlewares should also be
 * setup globally and outside any framework specific component or lifecycle event.
 * Or we risk registering the same middleware multiple times. Had analytics provided
 * an API to deregister middlewares, the logic could be move into the component and handled
 * as part of an effect clean up function.
 *
 * Guarded with `typeof window !== 'undefined'` because `addSourceMiddleware`
 * synchronously reads `window` at call time (via Segment's PreInitMethodCallBuffer),
 * which crashes SSR. Segment is browser-only; skipping registration on the
 * server is safe.
 */
if (typeof window !== 'undefined') {
  analytics.addSourceMiddleware(({ payload, next }) => {
    const utmData = extractUtmDataFromCookies()
    const segmentCampaign: SegmentCampaign = {
      name: utmData.utm_campaign,
      medium: utmData.utm_medium,
      source: utmData.utm_source,
      term: utmData.utm_term,
      content: utmData.utm_content,
    }
    payload.obj.context = {
      ...payload.obj.context,
      campaign: {
        ...segmentCampaign,
        ...payload.obj.context?.campaign,
      },
    }
    if (payload.obj.type === 'identify') {
      payload.obj.traits = {
        ...utmData,
        ...payload.obj.traits,
      }
    } else if (payload.obj.properties !== undefined) {
      payload.obj.properties = {
        environment: ENVIRONMENT,
        ...utmData,
        ...payload.obj.properties,
      }
    }

    next(payload)
  })
}

interface SegmentIdentifyProps {
  id: string
  email?: string
  name?: string
}

type SegmentContextType = {
  analytics: AnalyticsBrowser

  /**
   * Whether the module has been loaded.
   * `analytics.initialized` cannot be used, since that will only be set once the library finishes loading,
   * while `loaded` flag ensures the load method get called only once.
   */
  loaded: boolean

  identify?: (props: SegmentIdentifyProps) => Promise<void>
  track?: (event: string, props: unknown) => Promise<void>
  page?: (name?: string, properties?: Record<string, unknown>) => Promise<void>
  reset?: () => void
  alias?: (userId: string, previousId: string) => Promise<void>
}
const singletonContextValue: SegmentContextType = {
  analytics,
  loaded: false,
}

const loadAnalytics = () => {
  if (singletonContextValue.loaded) {
    return
  }

  singletonContextValue.loaded = true

  try {
    analytics
      .load(
        {
          writeKey: RuntimeConfigs.SEGMENT_WRITE_KEY,
        },
        {
          disable: !enabled,
          initialPageview: false,
        },
      )
      .catch(e => {
        logger.error('Segment analytics.load failed', e)
      })
  } catch (e) {
    logger.error('Segment analytics.load failed', e)
  }
}

const SegmentContext = createContext<SegmentContextType>(singletonContextValue)

interface Props {
  children: ReactNode
}

export const SegmentProvider = ({ children }: Props) => {
  const { pathname } = useLocation()
  const isAdminPage = isAdminRoutePathname(pathname)
  // Gate on operator PERMISSION, not merely the route — and on the SAME
  // predicate that controls entry to `/manage/*`. `AdminRouteGuard` checks
  // `hasScopesPermission(network, ['updateNetwork'])` and never looks at
  // `role.type` (the same scope drives the Administration nav entry), so anyone
  // it admits is a genuine operator of this community and is in scope for the
  // "our own customers" carve-out below.
  //
  // Deliberately NOT `useAuthMember().isAdmin` (`role.type === RoleType.admin`),
  // which is NARROWER than the route guard: an elevated moderator or a custom
  // role holding `updateNetwork` works in `/manage/*` indefinitely, so keying on
  // the role would blackout their analytics permanently — including the only two
  // `segment.track` sites in the repo, `Checkout Started` / `Checkout Completed`,
  // silently step-changing revenue dashboards with no way to backfill.
  //
  // This deliberately does NOT mirror the server-side PostHog gate
  // (`identifyAdminFromRequest`, `RoleType.admin`). The two answer different
  // questions: PostHog's is "whose identity may we bucket and forward", settled
  // by the compliance ruling; this one is "who is actually operating the admin
  // area", which is exactly what the route guard already decides.
  const { data: network } = useNetwork()
  const [canUpdateNetwork] = hasScopesPermission(network, ['updateNetwork'])

  // analytics-next queues any call made before `analytics.load()` and only
  // settles its promise once load flushes the queue. Off admin pages (or for
  // non-operators) load never happens (GDPR/permission guard below), so an awaited
  // identify/track/page/alias would hang forever — this froze the trial-paywall
  // checkout redirect on community pages. Drop those calls instead of queueing
  // them. `reset` is intentionally not gated: it runs on community pages to
  // de-identify the member after leaving the admin.
  const canTrack = enabled && isAdminPage && canUpdateNetwork

  useEffect(() => {
    // PRIVACY / GDPR — DO NOT REMOVE THIS GUARD.
    // We must never initialize the Segment client on community (end-user)
    // pages. We are not allowed to track our customers' customers: calling
    // `analytics.load()` makes the end user's browser fetch Segment's CDN
    // settings, which discloses their IP address — personal data under GDPR —
    // to a third-party sub-processor. Only our own customers (admins/staff,
    // who live under the admin routes) may be tracked, so the client is
    // bootstrapped exclusively on admin pages. See SegmentProvider.test.tsx
    // for the regression guard.
    //
    // Additionally gated on `updateNetwork`, the SAME scope `AdminRouteGuard`
    // requires: a member/moderator without it who briefly hits `/manage/*`
    // before the redirect must not trigger the CDN settings fetch either.
    if (isAdminPage && canUpdateNetwork) {
      loadAnalytics()
    }
  }, [isAdminPage, canUpdateNetwork])

  const identify = useCallback(
    async ({ id, email, name }: SegmentIdentifyProps) => {
      if (!canTrack || !id) {
        return
      }

      try {
        await analytics.identify(id, { email, name })
      } catch (e) {
        logger.error('Segment identify failed', { id, error: e })
      }
    },
    [canTrack],
  )

  const track = useCallback(
    async <T = object,>(event: string, props: T) => {
      if (!canTrack) {
        return
      }

      try {
        await analytics.track(event, {
          ...(props ?? {}),
        })
      } catch (e) {
        logger.error('Segment track failed', { event, error: e })
      }
    },
    [canTrack],
  )

  const page = useCallback(
    async (name?: string, properties?: Record<string, unknown>) => {
      if (!canTrack) {
        return
      }

      try {
        await analytics.page(name, properties)
      } catch (e) {
        logger.error('Segment page failed', { name, error: e })
      }
    },
    [canTrack],
  )

  const reset = useCallback(() => {
    try {
      analytics.reset()
    } catch (e) {
      logger.error('Segment reset failed', e)
    }
  }, [])

  const alias = useCallback(
    async (userId: string, previousId: string) => {
      if (!canTrack || !userId || !previousId) {
        return
      }
      try {
        await analytics.alias(userId, previousId)
      } catch (e) {
        logger.error('Segment alias failed', { userId, previousId, error: e })
      }
    },
    [canTrack],
  )

  const contextValue = useMemo(
    () => ({
      ...singletonContextValue,
      identify,
      track,
      page,
      reset,
      alias,
    }),
    [alias, identify, page, reset, track],
  )

  return (
    <SegmentContext.Provider value={contextValue}>
      {children}
    </SegmentContext.Provider>
  )
}

export const useSegment = () => {
  const context = useContext(SegmentContext)
  if (context === singletonContextValue && !RuntimeConfigs.IS_PROD) {
    logger.warn(
      'useSegment used outside SegmentProvider — telemetry calls will silently no-op',
    )
  }
  return context
}
